import React, { createContext, useContext, useState, useCallback } from 'react'
import PropTypes from 'prop-types'

// Successor to division-context. Same localStorage backing while both
// coexist, so a user landing on the new context sees the same selection
// they had under the old name. Once every call site is migrated and the
// backend stops accepting `division`, the legacy context can be removed.
//
// Values: 'EDC' | 'MS-RT' | 'all'. Stored as uppercase string; 'all'
// (or null) means no filter.

const STORAGE_KEY = 'selected_division'

const BRAND_CACHE_PREFIXES = [
  'projects_cache',
  'home_projects_cache',
  'designs_cache',
  'design_showcase_cache',
  'trending_wheels_cache',
  'trending_',
]

function clearBrandCaches() {
  const keysToRemove = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (BRAND_CACHE_PREFIXES.some((prefix) => key && key.startsWith(prefix))) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key))
}

const BrandFilterContext = createContext({
  brand: null,
  setBrand: () => {},
  clearBrand: () => {},
  isBrandSelected: false,
})

export const BrandFilterProvider = ({ children }) => {
  const [brand, setBrandState] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY)
      return v && v !== 'all' ? v : null
    } catch {
      return null
    }
  })

  const setBrand = useCallback((next) => {
    const value = next && next !== 'all' ? next.toUpperCase() : null
    clearBrandCaches()
    setBrandState(value)
    if (value) {
      localStorage.setItem(STORAGE_KEY, value)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [])

  const clearBrand = useCallback(() => {
    clearBrandCaches()
    setBrandState(null)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  return (
    <BrandFilterContext.Provider
      value={{
        brand,
        setBrand,
        clearBrand,
        isBrandSelected: !!brand,
      }}
    >
      {children}
    </BrandFilterContext.Provider>
  )
}

BrandFilterProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useBrandFilter = () => useContext(BrandFilterContext)
